// Tạo / sửa một bảng trong thư viện của Đại lý / CTV.
// P4: ô thấp hơn giá nhập của chính mình chỉ cảnh báo, vẫn lưu được. P3: sửa giá = phiên bản mới; tăng giá
// trên bảng đang giao cho cấp dưới phải hẹn trước N ngày.

import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { AlertTriangle, CalendarClock, Users } from 'lucide-react'
import { get, post, put } from '@/lib/api'
import { money } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  PriceListEditor,
  cellsBelow,
  emptyPriceListData,
  gridIssues,
  normalizePriceListData,
  priceListIssues,
  rowsPayload,
  sameRows,
  type PriceGrid,
  type PriceListData,
} from '@/components/PriceListEditor'
import {
  KIND_LABEL,
  errorDetails,
  errorMessage,
  fmtDateTime,
  sameData,
  scheduleFrom,
  toLocalInput,
  type LibraryDetail,
  type LibraryKind,
  type LibraryRow,
  type PriceCell,
  type SaveResponse,
} from './types'

interface IncreaseNotice {
  earliest: string
  increases: PriceCell[]
}

export interface CopySource {
  id: string
  label: string
  data: PriceListData
  grid: PriceGrid
}

export function PriceListFormDialog({
  grid,
  kind,
  list,
  buyData,
  buyGrid = null,
  copySources,
  onClose,
  onSaved,
}: {
  /** Hàng cân ban đầu: của bảng đang sửa; bảng mới thì chép hàng của giá nhập. */
  grid: PriceGrid
  kind: LibraryKind
  /** null = tạo mới */
  list: LibraryRow | null
  /** Giá nhập của tôi — ô nào thấp hơn sẽ tô đỏ (vẫn lưu được). */
  buyData: PriceListData | null
  /** Hàng cân của giá nhập (có thể khác hàng của bảng đang sửa). */
  buyGrid?: PriceGrid | null
  copySources: CopySource[]
  onClose: () => void
  onSaved: (res: SaveResponse) => void
}) {
  const original = useMemo(() => (list ? normalizePriceListData(grid, list.data) : emptyPriceListData(grid)), [grid, list])
  const [name, setName] = useState(list?.name ?? '')
  const [rows, setRows] = useState<PriceGrid>(grid)
  const [data, setData] = useState<PriceListData>(original)
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [notice, setNotice] = useState<IncreaseNotice | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [touched, setTouched] = useState(false)

  const detail = useQuery({
    queryKey: ['price-lists', 'library', list?.id],
    queryFn: () => get<{ priceList: LibraryDetail }>(`/price-lists/library/${list!.id}`),
    enabled: !!list,
  })
  const latest = detail.data?.priceList.versions[0]
  const latestScheduled = latest && new Date(latest.effectiveFrom).getTime() > Date.now() ? latest : null

  const issues = [...gridIssues(rows), ...priceListIssues(rows, data)]
  const belowBuy = buyData ? cellsBelow(rows, data, buyData, buyGrid) : []
  const increases = list ? cellsBelow(grid, original, data, rows) : []
  const dataChanged = !sameRows(grid, rows) || !sameData(original, data)
  const nameChanged = name.trim() !== (list?.name ?? '')

  const save = useMutation({
    mutationFn: (override?: { effectiveFrom: string }) => {
      const eff = override?.effectiveFrom ?? effectiveFrom
      if (!list) return post<SaveResponse>('/price-lists/library', { kind, name: name.trim(), data, rows: rowsPayload(rows) })
      return put<SaveResponse>(`/price-lists/library/${list.id}`, {
        ...(nameChanged ? { name: name.trim() } : {}),
        ...(dataChanged ? { data, rows: rowsPayload(rows) } : {}),
        effectiveFrom: eff && dataChanged ? new Date(eff).toISOString() : null,
      })
    },
    onSuccess: (res) => onSaved(res),
    onError: (e) => {
      const n = errorDetails<IncreaseNotice>(e, 'price_list.increase_notice_required')
      if (n?.earliest) {
        setNotice(n)
        setError(null)
        return
      }
      setError(errorMessage(e, 'Không lưu được bảng giá'))
    },
  })

  function submit(override?: { effectiveFrom: string }) {
    setTouched(true)
    setError(null)
    if (!name.trim()) return setError('Vui lòng đặt tên bảng giá')
    if (issues.length) return setError('Bảng giá chưa hợp lệ')
    if (list && !dataChanged && !nameChanged) return setError('Chưa có thay đổi nào để lưu')
    if (override) setEffectiveFrom(override.effectiveFrom)
    save.mutate(override)
  }

  const scheduleAt = notice ? toLocalInput(scheduleFrom(notice.earliest)) : ''

  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent className="sm:max-w-4xl" onInteractOutside={(e) => e.preventDefault()} {...(list ? {} : { 'aria-describedby': undefined })}>
        <DialogHeader className="pr-8">
          <DialogTitle>{list ? `Sửa bảng ${list.code}` : `Tạo bảng giá cho ${KIND_LABEL[kind]}`}</DialogTitle>
          {list && <DialogDescription>Phiên bản hiện tại v{list.version}.</DialogDescription>}
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
            <label className="block space-y-1.5">
              <span className="text-sm text-inkSoft">Tên bảng giá</span>
              <Input
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                  setError(null)
                }}
                placeholder={`VD: ${KIND_LABEL[kind]} tiêu chuẩn`}
              />
            </label>
            {!list && copySources.length > 0 && (
              <label className="block space-y-1.5">
                <span className="text-sm text-inkSoft">Bắt đầu từ</span>
                <select
                  className="h-11 w-full rounded-md border border-rule bg-raise px-2 text-sm text-ink desktop:h-10 sm:w-64"
                  defaultValue=""
                  onChange={(e) => {
                    const src = copySources.find((s) => s.id === e.target.value)
                    const g = src?.grid ?? rows
                    setRows(g)
                    setData(src ? normalizePriceListData(g, src.data) : emptyPriceListData(g))
                    setError(null)
                  }}
                >
                  <option value="">Bảng trống</option>
                  {copySources.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {list && (
            <div className={list.unitCount > 0 ? 'flex items-start gap-2 rounded-lg bg-warn/10 p-3 text-sm text-ink' : 'flex items-start gap-2 rounded-lg bg-paperSoft p-3 text-sm text-inkSoft'}>
              <Users className="mt-0.5 size-4 shrink-0" />
              <div>
                <p className="font-medium">
                  {list.unitCount > 0 ? `${list.unitCount} đơn vị đang dùng bảng này — sửa giá sẽ áp cho tất cả.` : 'Chưa đơn vị nào dùng bảng này.'}
                </p>
                {latestScheduled && (
                  <p className="mt-1 text-xs">
                    Giá đang hiện là phiên bản v{latestScheduled.version} đã hẹn áp dụng từ {fmtDateTime(latestScheduled.effectiveFrom)}.
                  </p>
                )}
              </div>
            </div>
          )}

          {touched && issues.length > 0 && (
            <ul className="list-disc space-y-0.5 rounded-lg bg-danger/5 py-2 pl-7 pr-3 text-xs text-danger">
              {issues.slice(0, 6).map((i) => (
                <li key={i}>{i}</li>
              ))}
              {issues.length > 6 && <li>… và {issues.length - 6} lỗi khác</li>}
            </ul>
          )}

          {!buyData && <p className="rounded-lg bg-warn/10 px-3 py-2 text-sm text-ink">Bạn chưa được giao bảng giá nhập.</p>}

          <PriceListEditor
            grid={rows}
            value={data}
            onChange={(d) => {
              // MoneyInput báo lại giá trị khi rời ô — chỉ xoá thông báo lỗi khi giá thật sự đổi.
              if (sameData(d, data)) return
              setData(d)
              setNotice(null)
              setError(null)
            }}
            onRowsChange={(g, d) => {
              setRows(g)
              setData(d)
              setNotice(null)
              setError(null)
            }}
            compareTo={buyData}
            compareGrid={buyGrid}
            compareLabel="Giá nhập"
            flagged={notice?.increases}
          />

          {belowBuy.length > 0 && (
            <p className="rounded-lg bg-warn/10 px-3 py-2 text-xs text-ink">{belowBuy.length} ô thấp hơn giá nhập của bạn.</p>
          )}

          {list && (
            <label className="block space-y-1.5 sm:max-w-xs">
              <span className="text-sm text-inkSoft">Áp dụng từ</span>
              <Input
                type="datetime-local"
                value={effectiveFrom}
                onChange={(e) => {
                  setEffectiveFrom(e.target.value)
                  setError(null)
                }}
              />
            </label>
          )}

          {list && list.unitCount > 0 && increases.length > 0 && !notice && (
            <p className="text-xs text-inkSoft">
              Bạn đang tăng giá ở {increases.length} ô.
            </p>
          )}

          {notice && (
            <div className="space-y-2 rounded-lg bg-warn/10 p-3 text-sm text-ink">
              <div className="flex gap-2">
                <CalendarClock className="mt-0.5 size-4 shrink-0 text-warn" />
                <div className="space-y-1">
                  <p className="font-medium">
                    Tăng giá ở {notice.increases.length} ô trên bảng đang giao cho cấp dưới — sớm nhất áp dụng từ {fmtDateTime(notice.earliest)}.
                  </p>
                  <ul className="text-xs text-inkSoft">
                    {notice.increases.slice(0, 5).map((c) => (
                      <li key={`${c.zone}:${c.cell}`}>
                        {c.label}: {money(c.child)} → {money(c.parent)}
                      </li>
                    ))}
                    {notice.increases.length > 5 && <li>… và {notice.increases.length - 5} ô khác</li>}
                  </ul>
                </div>
              </div>
              <Button size="sm" variant="outline" disabled={save.isPending} onClick={() => submit({ effectiveFrom: scheduleAt })}>
                Hẹn áp dụng từ {fmtDateTime(new Date(scheduleAt).toISOString())}
              </Button>
            </div>
          )}

          {error && (
            <p className="flex items-start gap-2 rounded-lg bg-danger/5 px-3 py-2 text-sm text-danger">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:space-x-0">
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Hủy
          </Button>
          <Button onClick={() => submit()} disabled={save.isPending || !buyData}>
            {save.isPending ? 'Đang lưu…' : list ? 'Lưu phiên bản mới' : 'Tạo bảng giá'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
